import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import {
	AlarisComplexTableComponent,
	AlarisDialogService,
	AlarisFilterManagerComponent,
	AlarisTableStateService,
	EditPanelWidth,
	TABLE_STATE_SERVICE_INJECTOR,
	TableEntityField
} from '@campaign-portal/components-library';
import { FilterType, Paging } from '@campaign-portal/namespace/common/rpc.params';
import { BehaviorSubject } from 'rxjs';
import { TableSettingsService } from '@helpers/api/table-settings.service';
import { TableTypes } from '@campaign-portal/namespace/entities/table-settings/specs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserService } from '../../auth/user.service';
import { EntityField, InputType } from '@campaign-portal/namespace/common/entityField';
import { ImportRate, Source } from '@helpers/types/models';
import { ImportRatesFieldsService } from './import-rates.fields.service';
import { ImportRatesService } from './import-rates.service';
import { ImportRatesDialogComponent } from './import-rates-dialog/import-rates-dialog.component';
import { DeleteRatesDialogComponent } from './delete-rates-dialog/delete-rates-dialog.component';
import { SourcesService } from '../sources/sources.service';

enum FilterVariables {
	ID = 'id',
	SOURCE = 'provider',
	MCCMNC = 'mccmnc',
	RATE = 'rate'
}

const source = new TableEntityField({
	id: null,
	name: 'import_rates.source',
	variable: FilterVariables.SOURCE,
	filterType: FilterType.LIKE,
	required: false,
	type: InputType.TEXT
});
const mccmnc = new TableEntityField({
	id: null,
	name: 'import_rates.mccmnc',
	variable: FilterVariables.MCCMNC,
	filterType: FilterType.LIKE,
	required: false,
	type: InputType.TEXT
});
const rate = new TableEntityField({
	id: null,
	name: 'import_rates.rate',
	variable: FilterVariables.RATE,
	filterType: FilterType.LIKE,
	required: false,
	type: InputType.TEXT
});
@Component({
	selector: 'app-import-rates',
	templateUrl: './import-rates.component.html',
	styleUrls: [
		'../../../../node_modules/@campaign-portal/components-library/src/assets/templates/table-complex/complex-table.component.scss',
		'import-rates.component.scss'
	],
	providers: [
		AlarisTableStateService,
		{ provide: TABLE_STATE_SERVICE_INJECTOR, useExisting: AlarisTableStateService }
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ImportRatesComponent extends AlarisComplexTableComponent<ImportRate> implements OnDestroy {
	private readonly headers: EntityField[] = [];
	override readonly pageSizeOptions = [25, 50, 100, 200];
	override readonly paging: Paging = {
		Skip: this.activePage,
		Take: this.pageSize
	};

	sources: Source[] = [];

	readonly exporting$ = new BehaviorSubject<boolean>(false);

	constructor(
		tableSettingsService: TableSettingsService,
		public override readonly dataService: ImportRatesService,
		public override readonly fieldsService: ImportRatesFieldsService,
		public sourcesService: SourcesService,
		private readonly dialog: AlarisDialogService,
		protected readonly user: UserService
	) {
		super(dataService, fieldsService, tableSettingsService, TableTypes.INTERNAL);
		this.headers = [source, mccmnc, rate];
		this.state
			.filters()
			.set(FilterVariables.ID, { enabled: false, filterType: FilterType.LIKE })
			.set(FilterVariables.SOURCE, { enabled: true, filterType: FilterType.LIKE })
			.set(FilterVariables.MCCMNC, { enabled: true, filterType: FilterType.LIKE })
			.set(FilterVariables.RATE, { enabled: true, filterType: FilterType.LIKE });
		this.sourcesService.load().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
	}

	override ngOnDestroy(): void {
		super.ngOnDestroy();
		this.exporting$.next(false);
		this.exporting$.complete();
	}

	deleteRates(): void {
		this.dialog
			.open<boolean>(DeleteRatesDialogComponent, { autoFocus: false, data: this.sources })
			.closed.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if (result) {
					this.refresh();
				}
			});
	}

	initiateImport(): void {
		this.dialog
			.open<boolean>(ImportRatesDialogComponent, { autoFocus: false })
			.closed.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if (result) {
					this.refresh();
				}
			});
	}

	override editTableFilter(): void {
		this.editPanel.open(AlarisFilterManagerComponent, EditPanelWidth.MD, {
			filtersChange: this.filtersChange,
			filters: this.state.filters,
			tHeaders: this.headers
		});
	}
}
